<template>
  <v-dialog
    v-model="showBanner"
    persistent
    max-width="900"
    :scrim="true"
    scrim-class="cookie-scrim"
  >
    <v-card class="cookie-consent-card pa-6">
      <v-card-title class="text-h5 font-weight-bold cookie-title pa-0 mb-4">
        About Cookies
      </v-card-title>

      <v-card-text class="pa-0 cookie-text">
        <p>
          By visiting this Site, you agree to our
          <a href="/terms-of-use" target="_blank" class="cookie-link">Terms of Use</a>,
          including its choice of law, forum selection, dispute resolution, and class-action waiver provisions.
          You also agree to our use of cookies and other technologies that monitor your use of the Site.
          Your interactions on the Site may be recorded or monitored by us or a third party with which we work.
          If you do not agree to this, please do not use our Site. For more information, please read our
          <a href="/privacy-policy" target="_blank" class="cookie-link">Privacy Policy</a>.
          Click "Got It" to agree or "Cookie Settings" to opt out.
        </p>
        <p class="mt-2">
          <a href="/cookie-notice" target="_blank" class="cookie-link">Cookie Notice</a>
        </p>
      </v-card-text>

      <v-card-actions class="pa-0 mt-6 d-flex flex-wrap align-center">
        <a href="#" @click.prevent="openSettings" class="cookie-settings-link">
          Cookies Settings
        </a>

        <v-spacer></v-spacer>

        <div class="d-flex ga-3">
          <v-btn
            color="primary"
            variant="flat"
            size="large"
            min-width="150"
            @click="rejectAll"
          >
            Reject All
          </v-btn>

          <v-btn
            color="primary"
            variant="flat"
            size="large"
            min-width="150"
            @click="acceptAll"
          >
            Got It
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Cookie Settings Dialog -->
  <v-dialog v-model="showSettings" max-width="600" persistent>
    <v-card class="pa-4">
      <v-card-title class="text-h6">Cookie Settings</v-card-title>

      <v-card-text>
        <v-list>
          <v-list-item>
            <template v-slot:prepend>
              <v-checkbox
                v-model="cookieSettings.necessary"
                disabled
                hide-details
              ></v-checkbox>
            </template>
            <v-list-item-title>Necessary Cookies</v-list-item-title>
            <v-list-item-subtitle>
              Required for the website to function. Cannot be disabled.
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item>
            <template v-slot:prepend>
              <v-checkbox
                v-model="cookieSettings.analytics"
                hide-details
              ></v-checkbox>
            </template>
            <v-list-item-title>Analytics Cookies</v-list-item-title>
            <v-list-item-subtitle>
              Help us understand how visitors interact with our website.
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item>
            <template v-slot:prepend>
              <v-checkbox
                v-model="cookieSettings.marketing"
                hide-details
              ></v-checkbox>
            </template>
            <v-list-item-title>Marketing Cookies</v-list-item-title>
            <v-list-item-subtitle>
              Used to deliver personalized advertisements.
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showSettings = false">Cancel</v-btn>
        <v-btn color="primary" variant="flat" @click="saveSettings">Save Settings</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const COOKIE_CONSENT_KEY = 'cookie_consent'

export default {
  name: 'CookieConsent',

  data() {
    return {
      showBanner: false,
      showSettings: false,
      cookieSettings: {
        necessary: true,
        analytics: false,
        marketing: false
      }
    }
  },

  mounted() {
    this.checkConsent()
  },

  methods: {
    checkConsent() {
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
      if (!consent) {
        // Small delay to let the app render first
        setTimeout(() => {
          this.showBanner = true
        }, 500)
      } else {
        // Load saved settings
        try {
          const saved = JSON.parse(consent)
          this.cookieSettings = { ...this.cookieSettings, ...saved }
        } catch (e) {
          // Invalid saved data, show banner again
          this.showBanner = true
        }
      }
    },

    acceptAll() {
      this.cookieSettings = {
        necessary: true,
        analytics: true,
        marketing: true
      }
      this.saveConsent()
    },

    rejectAll() {
      this.cookieSettings = {
        necessary: true,
        analytics: false,
        marketing: false
      }
      this.saveConsent()
    },

    openSettings() {
      this.showSettings = true
    },

    saveSettings() {
      this.showSettings = false
      this.saveConsent()
    },

    saveConsent() {
      const previousConsent = localStorage.getItem(COOKIE_CONSENT_KEY)
      const hadAccepted = previousConsent ? this.hadAcceptedBefore(previousConsent) : false
      const nowAccepts = this.cookieSettings.analytics || this.cookieSettings.marketing

      // Save the new consent
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(this.cookieSettings))
      this.showBanner = false

      // Migrate token if consent changed
      this.migrateTokenIfNeeded(hadAccepted, nowAccepts)

      // Emit event for other components to react
      this.$emit('consent-updated', this.cookieSettings)

      // Dispatch global event
      window.dispatchEvent(new CustomEvent('cookie-consent-updated', {
        detail: this.cookieSettings
      }))
    },

    hadAcceptedBefore(consentString) {
      try {
        const saved = JSON.parse(consentString)
        return saved.analytics === true || saved.marketing === true
      } catch {
        return false
      }
    },

    migrateTokenIfNeeded(hadAccepted, nowAccepts) {
      // If user changed from accepted to rejected, move token from localStorage to sessionStorage
      if (hadAccepted && !nowAccepts) {
        const token = localStorage.getItem('token')
        if (token) {
          sessionStorage.setItem('token', token)
          localStorage.removeItem('token')
        }
        const user = localStorage.getItem('user')
        if (user) {
          sessionStorage.setItem('user', user)
          localStorage.removeItem('user')
        }
      }
      // If user changed from rejected to accepted, move token from sessionStorage to localStorage
      else if (!hadAccepted && nowAccepts) {
        const token = sessionStorage.getItem('token')
        // Only migrate if not impersonated (impersonation always uses sessionStorage)
        if (token && sessionStorage.getItem('impersonated') !== 'true') {
          localStorage.setItem('token', token)
          sessionStorage.removeItem('token')
        }
        const user = sessionStorage.getItem('user')
        if (user && sessionStorage.getItem('impersonated') !== 'true') {
          localStorage.setItem('user', user)
          sessionStorage.removeItem('user')
        }
      }
    }
  }
}
</script>

<style scoped>
.cookie-consent-card {
  border-left: 4px solid #1565C0;
}

.cookie-title {
  color: #1565C0;
  background-color: rgba(21, 101, 192, 0.1);
  padding: 4px 8px !important;
  display: inline-block;
}

.cookie-text {
  color: #333;
  font-size: 14px;
  line-height: 1.6;
}

.cookie-link {
  color: #1565C0;
  text-decoration: underline;
  font-weight: 500;
}

.cookie-link:hover {
  color: #0D47A1;
}

.cookie-settings-link {
  color: #1565C0;
  text-decoration: underline;
  font-weight: 500;
  font-size: 16px;
}

.cookie-settings-link:hover {
  color: #0D47A1;
}

.cookie-scrim {
  background-color: rgba(0, 0, 0, 0.6) !important;
}
</style>
